import type { ToolUIPart } from "ai";
import { ChevronRightIcon, CircleSlashIcon } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { isValidElement } from "react";
import { cn } from "../../lib/utils";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "../ui/collapsible";
import { CodeBlock } from "./code-block";
import { Shimmer } from "./shimmer";

export type ToolProps = ComponentProps<typeof Collapsible>;

export const Tool = ({ className, ...props }: ToolProps) => (
	<Collapsible
		className={cn("group/tool not-prose w-full", className)}
		{...props}
	/>
);

export type ToolHeaderProps = {
	action?: string;
	title?: string;
	details?: ReactNode;
	type: ToolUIPart["type"];
	state: ToolUIPart["state"];
	/**
	 * The turn was cut off before this tool call finished (or before the user
	 * approved it). Rendered explicitly as a neutral "Interrupted" state.
	 */
	interrupted?: boolean;
	className?: string;
};

const getStatus = (status: ToolUIPart["state"]) => {
	const labels: Record<ToolUIPart["state"], string> = {
		"input-streaming": "Preparing",
		"input-available": "Running",
		"approval-requested": "Needs approval",
		"approval-responded": "Approved",
		"output-available": "Done",
		"output-error": "Failed",
		"output-denied": "Denied",
	};
	const active = status === "input-streaming" || status === "input-available";
	// A tool returning an error is normal agent control flow: the model can
	// inspect it, recover, and try something else. Keep that result available in
	// the expanded content without presenting it as a destructive turn failure.
	const attention = status === "approval-requested" || status === "output-denied";
	return { label: labels[status], active, attention };
};

export const ToolHeader = ({
	action,
	className,
	details,
	title,
	type,
	state,
	interrupted,
	...props
}: ToolHeaderProps) => (
	<ToolHeaderContent
		action={action}
		className={className}
		details={details}
		interrupted={interrupted}
		state={state}
		title={title}
		type={type}
		{...props}
	/>
);

function ToolHeaderContent({
	action,
	className,
	details,
	title,
	type,
	state,
	interrupted,
	...props
}: ToolHeaderProps) {
	const status = getStatus(state);
	const active = interrupted ? false : status.active;
	const attention = interrupted ? true : status.attention;
	const label = interrupted ? "Interrupted" : status.label;
	const fallbackTitle = type.split("-").slice(1).join("-").trim();
	const displayTitle = title?.trim() || fallbackTitle || "Tool";
	const displayAction = action?.trim() || displayTitle;

	return (
		<CollapsibleTrigger
			className={cn(
				"type-conversation group/header flex min-h-6 w-fit max-w-full items-center gap-1.5 rounded-sm text-left transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/50",
				className,
			)}
			{...props}
		>
			<span className="shrink-0 font-medium text-foreground/75 group-hover/header:text-foreground/90">
				{active ? (
					<Shimmer as="span">
						{displayAction}
					</Shimmer>
				) : (
					displayAction
				)}
			</span>
			{action !== undefined && displayTitle !== displayAction && (
				<span className="min-w-0 truncate text-muted-foreground">{displayTitle}</span>
			)}
			{details !== undefined && (
				<span className="flex min-w-0 shrink items-center gap-1 truncate text-muted-foreground">
					{details}
				</span>
			)}
			{attention && (
				<span className="type-caption flex shrink-0 items-center gap-1.5 text-muted-foreground">
					{interrupted && <CircleSlashIcon className="size-3" />}
					{label}
				</span>
			)}
			<ChevronRightIcon className="size-3.5 shrink-0 text-muted-foreground/70 transition-transform group-data-[state=open]/header:rotate-90" />
		</CollapsibleTrigger>
	);
}

export type ToolContentProps = ComponentProps<typeof CollapsibleContent>;

export const ToolContent = ({ className, ...props }: ToolContentProps) => (
	<CollapsibleContent
		className={cn(
			"text-popover-foreground outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-1 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-top-1",
			className,
		)}
		{...props}
	/>
);

export type ToolInputProps = ComponentProps<"div"> & {
	input: ToolUIPart["input"];
};

export const ToolInput = ({ className, input, ...props }: ToolInputProps) => (
	<div className={cn("space-y-1.5 overflow-hidden py-2", className)} {...props}>
		<h4 className="type-caption font-medium text-muted-foreground">Input</h4>
		<div className="overflow-hidden rounded-md border border-hairline bg-background/55">
			<CodeBlock
				className="rounded-none border-0 [&_code]:text-xs! [&_pre]:p-3! [&_pre]:text-xs!"
				code={JSON.stringify(input, null, 2)}
				language="json"
			/>
		</div>
	</div>
);

export type ToolOutputProps = ComponentProps<"div"> & {
	output: ToolUIPart["output"];
	errorText: ToolUIPart["errorText"];
	showEmpty?: boolean;
};

export const ToolOutput = ({
	className,
	output,
	errorText,
	showEmpty = false,
	...props
}: ToolOutputProps) => {
	if (output == null && !errorText && !showEmpty) {
		return null;
	}

	let Output = <div>{output as React.ReactNode}</div>;

	if (output == null) {
		Output = (
			<CodeBlock
				className="rounded-none border-0 [&_code]:text-xs! [&_pre]:p-3! [&_pre]:text-xs!"
				code="null"
				language="json"
			/>
		);
	} else if (typeof output === "object" && !isValidElement(output)) {
		Output = (
			<CodeBlock
				className="rounded-none border-0 [&_code]:text-xs! [&_pre]:p-3! [&_pre]:text-xs!"
				code={JSON.stringify(output, null, 2)}
				language="json"
			/>
		);
	} else if (typeof output === "string") {
		Output = (
			<CodeBlock
				className="rounded-none border-0 [&_code]:text-xs! [&_pre]:p-3! [&_pre]:text-xs!"
				code={output}
				language="json"
			/>
		);
	}

	return (
		<div className={cn("space-y-1.5 py-2", className)} {...props}>
			<h4 className="type-caption font-medium text-muted-foreground">Output</h4>
			<div className="overflow-x-auto rounded-md border border-hairline bg-background/55 text-xs [&_table]:w-full">
				{Output}
			</div>
			{errorText && (
				<>
					<h4 className="type-caption font-medium text-destructive">Error</h4>
					<div className="overflow-x-auto whitespace-pre-wrap rounded-md border border-destructive/20 bg-destructive/5 p-3 text-destructive text-xs">
						{errorText}
					</div>
				</>
			)}
		</div>
	);
};
